import { z } from 'zod';

/**
 * A player's preferred tee box (User.preferredTee). Tee boxes differ per course, so the preference
 * is a course-independent colour key; on a tournament's layout it is matched to one of that
 * layout's tees by colour, then by name (`matchPreferredTee`). null / absent = automatic (the
 * tournament's tee rules decide, §8.5).
 */
export const PreferredTeeSchema = z.enum(['BLACK', 'BLUE', 'WHITE', 'GOLD', 'RED']);
export type PreferredTee = z.infer<typeof PreferredTeeSchema>;
export const PREFERRED_TEES = PreferredTeeSchema.options;

/** Swatch colour of each key (UI chips; same palette as the seeded course tees). */
export const PREFERRED_TEE_SWATCH: Record<PreferredTee, string> = {
  BLACK: '#111827',
  BLUE: '#1D4ED8',
  WHITE: '#F3F4F6',
  GOLD: '#CA8A04',
  RED: '#DC2626',
};

const NAME_PATTERNS: Record<PreferredTee, RegExp> = {
  BLACK: /(black|champion|đen|블랙|검정|黑|ブラック)/i,
  BLUE: /(blue|xanh(?!\s*lá)|블루|파랑|蓝|藍|ブルー)/i,
  WHITE: /(white|trắng|화이트|흰|白|ホワイト)/i,
  GOLD: /(gold|yellow|senior|vàng|골드|옐로|노랑|金|黄|ゴールド|イエロー)/i,
  RED: /(red|lad(y|ies)|wom[ae]n|đỏ|레드|레이디|빨강|红|紅|赤|レッド|レディ)/i,
};

/** Colour key of a hex colour (#rgb / #rrggbb), or null when it is none of the five. */
export function teeColourKey(color: string | null | undefined): PreferredTee | null {
  const m = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec((color ?? '').trim());
  if (!m) return null;
  const hex =
    m[1]!.length === 3
      ? m[1]!
          .split('')
          .map((c) => c + c)
          .join('')
      : m[1]!;
  const n = parseInt(hex, 16);
  const r = ((n >> 16) & 255) / 255;
  const g = ((n >> 8) & 255) / 255;
  const b = (n & 255) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  const d = max - min;
  const s = d === 0 ? 0 : d / (1 - Math.abs(2 * l - 1));
  if (l < 0.2) return 'BLACK';
  if (l > 0.9) return 'WHITE';
  if (s < 0.2 || d < 0.12) {
    if (l < 0.35) return 'BLACK';
    if (l > 0.75) return 'WHITE';
    return null;
  }
  let h: number;
  if (max === r) h = ((g - b) / d) % 6;
  else if (max === g) h = (b - r) / d + 2;
  else h = (r - g) / d + 4;
  h = (h * 60 + 360) % 360;
  if (h <= 15 || h >= 340) return 'RED';
  if (h >= 35 && h <= 65) return 'GOLD';
  if (h >= 190 && h <= 255) return 'BLUE';
  return null;
}

/** Colour key a tee reads as: its colour first, then its name. */
export function teeKeyOf(tee: { name: string; color?: string | null }): PreferredTee | null {
  return (
    teeColourKey(tee.color) ?? PREFERRED_TEES.find((k) => NAME_PATTERNS[k].test(tee.name)) ?? null
  );
}

/**
 * The tee of a layout matching a preference: the first tee whose colour is that key, else the
 * first whose name says it. null = no match (the caller falls back to the tee rules).
 */
export function matchPreferredTee<T extends { id: string; name: string; color?: string | null }>(
  preference: PreferredTee | null | undefined,
  tees: readonly T[],
): T | null {
  if (!preference) return null;
  return (
    tees.find((t) => teeColourKey(t.color) === preference) ??
    tees.find((t) => NAME_PATTERNS[preference].test(t.name)) ??
    null
  );
}
